import React, { useEffect, useMemo, useState } from 'react';

import { Flex, Stack, useToast } from '@chakra-ui/react';
import { cloneDeep } from 'lodash';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { useToggle } from 'react-use';

import { createChecklist, getChecklist, updateChecklist } from 'api/checklists';
import { breadcrumbs } from 'constants/routes';
import BreadcrumbMenu from 'containers/Breadcrumb';
import { BaseField, FormFooter, FormHead } from 'containers/Checklists';
import { motorcycle, vehicle } from 'containers/Checklists/constants';
import Page from 'containers/Page';
import { ContentRoutes } from 'enums/routes';
import { GeneralStatus } from 'enums/status';
import {
  AccessTypes, ChecklistFieldTypes, ChecklistTypes, MethodTypes,
} from 'enums/types';
import { policy } from 'polices';
import ChecklistResolver from 'validations/checklist';

const baseChecklist = {
  name: 'Novo Checklist',
  description: 'Descrição',
  type: ChecklistFieldTypes.Checkbox,
  options: ['Opção 1', 'Opção 2', 'Opção 3'],
  inputs: [{
    label: 'Item 1',
    active: true,
  }],
};

export default function Checklists() {
  const { id, executionId } = useParams<GeneralRoute & {executionId: string}>();
  const { push } = useHistory();
  const form = useForm({
    mode: 'all',
    resolver: ChecklistResolver,
  });
  const policies = policy('checklists');
  const toast = useToast();
  const [isEditing, toggleEdition] = useToggle(false);
  const [execution, setExecution] = useState<any>();
  const [, forceReRender] = useToggle(false);

  useEffect(() => {
    if (policies(MethodTypes.View) === AccessTypes.Disallow) {
      push('/403');
    }
  }, []);

  const { refetch, isFetching } = useQuery(['checklists', id], () => getChecklist({ id }), {
    enabled: !!id,
    onSuccess: ({ data }) => {
      reset(data);
      setValue('type', { label: data.type, value: data.type });
      setValue('regional', { label: data.regional?.name, id: data.regional?.id });
      setValue('info.startDate', data.nextExecution);
      data.weekDays?.forEach((day) => {
        setValue(`day.${day}`, true);
      });
      setExecution(data.executions?.find((exec) => exec.id === Number(executionId)));
    },
  });

  const { mutate, isLoading: isSaving } = useMutation(async (data: any) => {
    const body = {
      ...data,
      status: GeneralStatus.Active,
      type: data.type.value,
      nextExecution: data.info.startDate,
      weekDays: data?.day?.map((day, index) => day && index).filter(
        (day) => Number.isSafeInteger(day)),
    };
    return (id ? updateChecklist : createChecklist)({ id, data: body });
  },
  {
    onSuccess: ({ data }) => {
      if (!id) {
        window.location.replace(`/${ContentRoutes.Checklists}/${data.id}`);
        refetch();
        toggleEdition();
      } else {
        window.location.reload();
      }
    },
    onError: (error: any) => {
      if (error.errors) {
        error.errors.forEach((err) => {
          form.setError(err.field, err);
        });
      } else {
        toast({
          title: 'Ocorreu um erro.',
          description: error.message,
          status: 'error',
          duration: 5000,
          position: 'top-right',
          isClosable: true,
        });
      }
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
  } = form;

  const { append, fields } = useFieldArray({
    control,
    name: 'fields',
  });

  const checklist = useMemo(() => getValues(), [getValues]);
  const type = watch('type');
  const checklistData = useMemo(
    () => (type?.value === ChecklistTypes.Motorcycle ? motorcycle : vehicle),
    [type]);

  useEffect(() => {
    if (!id && checklistData) {
      reset(cloneDeep({
        ...checklistData,
        type: {
          value: checklistData.type,
          label: checklistData.type,
        },
      }));

      if (!isEditing) {
        toggleEdition();
      }
    }
  }, [checklistData]);

  return (
    <Page isLoading={isFetching}>
      <Flex
        alignSelf="center">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit((data) => mutate(data))}>
            <Flex
              mx="auto"
              w="100%"
              p={5}
              flexDirection="column">
              <BreadcrumbMenu paths={[
                ...breadcrumbs[ContentRoutes.Checklists] || [],
                {
                  path: `/${ContentRoutes.Checklists}/${id}`,
                  label: checklist.name,
                },
              ]}
              />
              <FormHead
                execution={execution}
                isEditing={isEditing}
                onEdit={toggleEdition}
              />
              <Stack
                minW="1048px"
                spacing={6}>
                {useMemo(() => fields?.map((item:any, index) => {
                  const isActive = watch(`fields.${index}.active`);
                  return (
                    <BaseField
                      execution={execution}
                      allowEdition={isEditing}
                      key={`checklist.${id}.field.${index}`}
                      index={index}
                      isInactive={!isActive}
                      forceParentReRender={forceReRender}
                      {...item}
                    />
                  );
                }), [fields, isEditing, execution])}
              </Stack>
              <FormFooter
                isEditing={isEditing}
                isSaving={isSaving}
                onAddGroup={() => append(baseChecklist)}
              />
            </Flex>
          </form>
        </FormProvider>
      </Flex>
    </Page>
  );
}
